'use strict';

define('vb/private/stateManagement/switcherBridge',[
  'knockout',
  'ojs/ojarraydataprovider',
  'vb/private/stateManagement/switcherElement',
  'vb/private/stateManagement/componentBridge',
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/stateManagement/switcherRootRouter',
  'vb/private/stateManagement/router',
  'vb/private/history',
  'vb/private/constants',
  'vb/private/log',
], (ko, ArrayDataProvider, SwitcherElement, ComponentBridge,
  VbRouter, SwitcherRootRouter, Router, History, Constants, Log) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/page').default} Page
   * @typedef {import('vb/private/stateManagement/router').NavigateResult} NavigateResult
   * @typedef {import('ojs/ojcomposite').ViewModelContext} ViewModelContext
   */
  const logger = Log.getLogger('/vb/stateManagement/switcherBridge');

  /**
   * @type {VbRouter}
   */
  let originalRootInstance;

  /**
   * Given a container, returns the page that contains it. In the case of switcher in a fragment, that page
   * is the parent of the fragment.
   * Throws an error if there are no page in the hierarchy or if the switcher is in a layout.
   * Since a page cannot be embedded in a layout, as soon as we find a page we can return it,
   * but if we find a layout before that, it's an error.
   * @param {*} container - The container to find the parent page for
   * @returns {Page} The parent page of the container
   */
  const findParentPage = (container) => {
    if (!container) {
      throw new Error('oj-vb-switcher must be used inside a page or a fragment');
    }

    const { className } = container;
    if (className.includes('Page')) {
      // Found the page that contains the switcher, return it
      return container;
    }

    if (className.includes('Layout')) {
      throw new Error('oj-vb-switcher cannot be used inside a layout.');
    }

    return findParentPage(container.parent);
  };

  /**
   * Implementation of the switcher component
   */
  class SwitcherBridge extends ComponentBridge {
    /**
     * @param {Container} container - The container that contains the oj-vb-switcher component,
     * could be a page or a fragment
     * @param {Object} props
     */
    constructor(container, props) {
      super();
      const allSwitcherElements = document.getElementsByTagName('oj-vb-switcher');
      if (allSwitcherElements.length > 1) {
        throw new Error('Cannot have more than one oj-vb-switcher in an application.');
      }

      // In case the switcher is in a fragment, find the page that contains this fragment
      const parentPage = findParentPage(container);

      /** @type {Element} */
      this.composite = allSwitcherElements[0]; // eslint-disable-line prefer-destructuring

      // Verify that the parent page does not already have a nested flow
      const allFlows = Object.keys(parentPage.flows);
      if (allFlows.length > 0) {
        allFlows.forEach((flowId) => {
          const flow = parentPage.flows[flowId];
          try {
            flow.dispose();
          // eslint-disable-next-line no-empty, no-unused-vars
          } catch (err) {}
        });

        // It's important to undefine the router state, otherwise the vbRouter code
        // will try to undefine this state on the first navigation which will
        // invoke canExit and fail since the page is gone.
        // Also the router should not be removed because of case like the service app
        // where the same page can "switch" between an oj-vb-switcher or an oj-vb-content tag
        if (parentPage.router) {
          // eslint-disable-next-line no-underscore-dangle
          parentPage.router._stateId(null);
        }
      }

      /**
       * Bind the parent page to this switcher bridge instance
       * @type {SwitcherBridge}
       */
      parentPage.switcherBridge = /** @type {*} */ (this);

      /**
       * The parent page of the switcher
       * @type {Page}
       */
      this.parent = parentPage;

      /**
       * Object to map switcherElement by id
       */
      this._switcherElts = {};

      /**
       * An observable array used by the Array DataProvider to store the oj-module index
       */
      this._modules = ko.observableArray();
      /**
       * An array DataProvider used by the oj-bind-for-each to enumerate through the oj-module
       */
      this._moduleADP = new ArrayDataProvider(this._modules, { keyAttributes: 'index' });

      /**
       * An array of oj-module configuration object used to configure each oj-module in the oj-bind-each
       * The value is modified by the rootPage when navigating in the switcher
       */
      this.moduleConfigs = [];

      /**
       * An array of switcher element id which index matches the oj-module
       */
      this.elementIds = [];

      this.props = props;
    }

    /**
     * @param {ViewModelContext} context
     */
    connected(context) {
      // When the switcher component is connected, replace how the root router is retrieved
      // This is to support getting the switcher element root router instead of the base root router
      // when history.popstate is called.
      originalRootInstance = VbRouter.rootInstance;
      Object.defineProperty(VbRouter, 'rootInstance', {
        get() {
          const { routerName } = SwitcherElement.getFromHistory();
          if (routerName) {
            return SwitcherRootRouter.getRootInstanceFromName(routerName);
          }

          return originalRootInstance;
        },
        enumerable: true,
        configurable: true,
      });

      super.connected(context);
    }

    /**
     * @param {HTMLCollection|undefined} domNodes
     */
    disconnected(domNodes) {
      // Remove all elements in the switcher
      this.arrayRemove(this.props.data.data);
      // Restore the original root router.
      Object.defineProperty(VbRouter, 'rootInstance', {
        value: originalRootInstance,
        enumerable: true,
        configurable: true,
      });

      // Unbind the parent page from this switcher bridge instance
      this.parent.switcherBridge = null;

      super.disconnected(domNodes);
    }

    /**
     * Return the ArrayDataProvider that enumerate the oj-module to be used in the oj-bind-for-each
     *
     * @return {ArrayDataProvider}  the module array data provider.
     */
    getModuleArrayDataProvider() {
      return this._moduleADP;
    }

    /**
     * Switch the flow displayed when the current item changes.
     *
     * @param  {Object}  eventContext
     * @param  {string}  eventContext.value The id of the switcher element to make current
     * @param  {string}  eventContext.previousValue The previous switcher id
     * @param  {string}  eventContext.updatedFrom When true, do not push a state change on the browser
     * @return {Promise<{indexToShow: number|undefined, indexToHide: number|undefined}>}
     * a promise to the indexes of the elements to show and hide
     */
    currentItemChanged(eventContext) {
      const { previousValue, value, updatedFrom } = eventContext;
      // if the currentItem change was initiated internally
      const isNotExternal = updatedFrom !== 'external';
      const id = value;
      const previousId = previousValue;

      const results = {
        indexToShow: undefined,
        indexToHide: this.getElementIndex(previousId),
      };

      return Promise.resolve().then(() => {
        // a null item means to hide the current selection
        if (!id) {
          return undefined;
        }

        const switcherElt = this.getSwitcherElement(id);
        if (!switcherElt) {
          return undefined;
        }

        results.indexToShow = switcherElt.index;

        const rootPage = switcherElt.root;

        // This is the case where it's switching to a new element
        if (!rootPage.isInitialized()) {
          return switcherElt.initialize();
        }

        const currentPage = switcherElt.getCurrentPage();

        return rootPage.rootRouter.updateState(currentPage && currentPage.fullPath, isNotExternal)
          .then(() => {
            if (currentPage) {
              // Assign the $application.currentPage variable
              rootPage.application.updateApplicationCurrentPageVariable(currentPage);
            }
          });
      }).then(() => results);
    }

    /**
     * Triggered in response of element(s) added to the array of switcher elements
     *
     * @param {Array<Object>} data The array of element(s) to add
     */
    arrayAdd(data) {
      if (Array.isArray(data) && data.length > 0) {
        logger.info('Adding', data.length, 'switcher element(s):', data);
        data.forEach((element) => {
          try {
            const switcherElt = new SwitcherElement(element, this);

            // Find the first available spot
            let index = this.elementIds.findIndex((id) => id === null);
            // and if there isn't one, add a new spot
            if (index === -1) {
              index = this.elementIds.length;

              this.moduleConfigs.push(ko.observable(Constants.blankModuleConfig));
              this._modules.push({ index });
              this.elementIds.push(null);
            }

            switcherElt.index = index;

            const { id } = switcherElt;
            this.elementIds[index] = id;
            this._switcherElts[id] = switcherElt;
          } catch (error) {
            // In case of error like when the id already exist, log the error but
            // don't throw so that other element can be added and the switcher is in consistent state
            logger.error(error);
          }
        });
      }
    }

    /**
     * Remove one SwitcherElement given its id.
     *
     * @param {String} idToRemove
     * @return {Promise}
     * @private
     */
    _removeElement(idToRemove) {
      // Claim removal synchronously before yielding: concurrent removal/currentItem work in the
      // same turn must see this in-flight element. Do not use getSwitcherElement(), which hides
      // elements already marked isRemoving, or defer this lookup into Promise.resolve().then().
      const switcherElt = this._switcherElts[idToRemove];
      if (!switcherElt) {
        return Promise.resolve();
      }

      if (switcherElt.removalPromise) {
        return switcherElt.removalPromise;
      }

      // Make the element unavailable before starting its asynchronous exit. A currentItem
      // change can otherwise retrieve this element while it is exiting and navigate its root
      // router after a replacement tab has already been selected.
      switcherElt.isRemoving = true;

      const removalPromise = Promise.resolve().then(() => {
        const rootPage = switcherElt.root;

        if (rootPage.isInitialized()) {
          // TODO: Only call exit here and let disconnect do the reset of the delete
          return this._deleteElement(switcherElt);
        }

        this.elementIds[switcherElt.index] = null;
        switcherElt.dispose();
        delete this._switcherElts[idToRemove];
        return undefined;
      }).catch((error) => {
        // Preserve the element when its exit did not complete so that it can be retried.
        switcherElt.isRemoving = false;
        switcherElt.removalPromise = undefined;
        logger.error('Error while removing a switcher element', error);
        return undefined;
      });

      switcherElt.removalPromise = removalPromise;
      return removalPromise;
    }

    /**
     * Remove all SwitcherElements given an array of id
     *
     * @param {Array<String>} ids
     * @return {Promise}
     * @private
     */
    _removeElements(ids) {
      return Promise.all(ids.map((idToRemove) => this._removeElement(idToRemove)));
    }

    /**
     * Triggered in response of element(s) deleted from the array of switcher elements
     *
     * @param {Array<Object>} data The array of element(s) to remove
     * @return {Promise}
     */
    arrayRemove(data) {
      if (Array.isArray(data) && data.length > 0) {
        logger.info('Removing', data.length, 'switcher element(s):', data);
        return this._removeElements(data.map((elt) => elt.id));
      }
      return Promise.resolve();
    }

    /**
     * Triggered in response of the array data provider being refreshed
     *
     * @param {Object} adp The Array Data Provider
     * @return {Promise}
     */
    refresh(adp) {
      logger.info('Refreshing data');

      // Remove all existing switcher elements
      return this._removeElements(Object.keys(this._switcherElts)).then(() => {
        this.arrayAdd(adp.data);
      });
    }

    /**
     * A function recursively call a function on container
     *
     * @param   {String} callback  The callback function name
     * @param   {Container} container  The container
     * @return  {Promise}
     * @private
     */
    _invokeCallback(callback, container) {
      return Promise.resolve().then(() => {
        if (container) {
          return container[callback]().then(() => {
            const { parent } = container;
            if (parent && !parent.isNavigationBoundary()) {
              return this._invokeCallback(callback, parent);
            }
            return undefined;
          });
        }
        return undefined;
      });
    }

    /**
     * Function to recursively invoke canExit on all container
     *
     * @param   {Container}  container  The container
     * @return  {Promise}
     */
    invokeCanExit(container) {
      return container.canExit().then((result) => {
        // If can exit, continue up the container hierarchy up to the root page
        if (result) {
          const { parent } = container;
          if (parent && !parent.isNavigationBoundary()) {
            return this.invokeCanExit(parent);
          }
        }
        return result;
      });
    }

    /**
     * Delete a switcher element.
     * This is called after an element is removed from the switcher
     * @param {SwitcherElement} switcherElt
     * @return {Promise}
     * @private
     */
    _deleteElement(switcherElt) {
      return Promise.resolve().then(() => {
        if (switcherElt) {
          const currentPage = switcherElt.getCurrentPage();

          return this._invokeCallback('exit', /** @type {*} */ (currentPage)).then(() => {
            this.elementIds[switcherElt.index] = null;
            switcherElt.dispose();

            delete this._switcherElts[switcherElt.id];
          });
        }

        return undefined;
      });
    }

    /**
     * Trigger a navigation on the page of the current switcher element
     *
     * @param  {Object}  options  The navigate options, same as for a navigate action
     * @return {Promise<NavigateResult>} a promise that resolve when navigation is complete
     */
    navigate(options) {
      return Promise.resolve().then(() => {
        const switcherElt = this.getCurrentSwitcherElement();
        const rootPage = switcherElt && switcherElt.root;
        if (rootPage && rootPage.isInitialized()) {
          const leafPage = switcherElt.getCurrentPage();
          if (leafPage) {
            return Router.queueNavigate(/** @type {*} */ (leafPage), options);
          }
        }

        return Router.DID_NOT_NAVIGATE;
      });
    }

    /**
     * Return a promise that resolves to a boolean that is true when all the
     * containers inside the element accept the delete operation.
     * This is going through the same process as beforeExit
     *
     * @param   {String}  id the id of the switcher element to remove
     * @return  {Promise} a promise to a boolean
     */
    close(id) {
      return Promise.resolve().then(() => {
        if (id) {
          const switcherElt = this.getSwitcherElement(id);
          const rootPage = switcherElt && switcherElt.root;
          if (rootPage && rootPage.isInitialized()) {
            // Cannot delete an item that is not current
            if (id !== this.currentItem) {
              return false;
            }

            const leafPage = switcherElt.getCurrentPage();
            if (leafPage) {
              return this.invokeCanExit(/** @type {*} */ (leafPage));
            }
          }
        }

        return true;
      });
    }

    /**
     * Called when the browser state stack changes
     * @return {boolean} true when the event is handled and the super should not be called.
     */
    handlePopState() {
      // Retrieve potential switcher element info from the browser history
      const { id } = SwitcherElement.getFromHistory();

      const executeDefault = this.dispatchBeforeHandlePopstateEvent(id);

      // By changing the value of currentItem, the switcher will automatically switch
      if (id && this.currentItem !== id
        // Make sure the element has not been previously deleted
        && this.getSwitcherElement(id)) {
        if (executeDefault) {
          this.currentItem = id;
        } else {
          Router.clearBusyState();
        }

        return true;
      }

      // when executeDefault is true, returns false to have the super class execute the default handling
      return !executeDefault;
    }

    /**
     * Dispatch the dispatchBeforeHandlePopstate event
     *
     * @param   {String} item the id of the element that will be switched to
     * @return  {boolean} true if the default handling of the event should be executed
     */
    dispatchBeforeHandlePopstateEvent(item) {
      const vbState = History.getStateBeforeHistoryPop().state || {};
      const previousPagePath = vbState.pagePath;
      const eventParams = {
        bubbles: true,
        cancelable: true,
        detail: {
          item,
          pagePath: History.getPagePath(),
          previousItem: this.currentItem,
          previousPagePath,
        },
      };

      return this.composite.dispatchEvent(new CustomEvent('vbBeforePopState', eventParams));
    }

    /**
     * Dispatch the event after the displayed switcher content changes.
     *
     * @param   {String} item the id of the displayed element
     * @param   {String} previousItem the id of the previously displayed element
     */
    dispatchSwitcherContentChangedEvent(item, previousItem) {
      const eventParams = {
        bubbles: true,
        cancelable: false,
        detail: {
          item,
          previousItem,
        },
      };

      this.composite.dispatchEvent(new CustomEvent('vbSwitcherContentChanged', eventParams));
    }

    /**
     * Retrieve the switcher component currentItem property
     *
     * @type {String}
     */
    get currentItem() {
      return this.props.currentItem;
    }

    /**
     * Change the switcher component currentItem property
     * Because it's a write-back property assignment need to be done on props.currentItem
     *
     * @type {String}
     */
    set currentItem(value) {
      this.props.currentItem = value;
    }

    /**
     * Retrieve a switcher element given its id
     *
     * @param  {String} id  The element identifier
     * @return {SwitcherElement}  the switcher element
     */
    getSwitcherElement(id) {
      const switcherElt = this._switcherElts[id];
      return switcherElt && !switcherElt.isRemoving ? switcherElt : undefined;
    }

    /**
     * Return the ojModule index from an element id
     *
     * @param  {String}  id  a switcher element id
     * @return {number}  The element index in the array of ojModule
     */
    getElementIndex(id) {
      const { index } = this.getSwitcherElement(id) || {};
      return index;
    }

    /**
     * Retrieve the current switcher element
     *
     * @return {SwitcherElement} the current switcher element
     */
    getCurrentSwitcherElement() {
      return this.getSwitcherElement(this.currentItem);
    }

    /**
     * Return the oj-module configuration object given the index of the
     * oj-module in the oj-bind-for-each
     *
     * @param  {Number} index The index in the array ojModule
     * @return {Object} The ojModule configuration at this index
     */
    getModuleConfigFromIndex(index) {
      return this.moduleConfigs[index];
    }
  }

  return SwitcherBridge;
});

